import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';

import { PortalAuthService } from '../auth/portal-auth.service';
import { LanguageService } from '../core-services/language.service';
import { ThemeService } from '../core-services/theme.service';
import { PortalUser } from '../domain/portal-user.model';
import { UserApiService } from './user-api.service';

/** localStorage flag: this browser already handed its own pre-move choices to the account. */
const MIGRATED_KEY = 'portal.settingsMigrated';

/**
 * Language and appearance are chosen once in My-SPA and apply in every app, so the account is the single
 * source of truth and this app only reads it. localStorage keeps a copy purely so the first paint after a
 * reload already looks right.
 *
 * Before the move each app kept its own choice. The first sync in a browser therefore hands a choice that
 * differs from the account (or an appearance the account does not have yet) to the account once, so nobody
 * loses a setting they made. After that the account value always wins.
 */
@Injectable({ providedIn: 'root' })
export class AccountSettingsService {
  private synced = false;
  private watching = false;

  constructor(
    private readonly language: LanguageService,
    private readonly theme: ThemeService,
    private readonly userApi: UserApiService,
    private readonly auth: PortalAuthService,
  ) {}

  /** Applies the account's settings, once per page load; later calls from other places are ignored. */
  sync(user: PortalUser): void {
    if (this.synced) return;
    this.synced = true;

    const push: Partial<PortalUser> = this.migrated() ? {} : this.localChoices(user);
    this.language.applyServerLanguage(push.language ?? user.language);
    this.theme.applyServerTheme(push.theme ?? user.theme);

    if (Object.keys(push).length === 0) {
      this.markMigrated();
      return;
    }
    this.userApi.updateProfile(push).subscribe({
      next: () => this.markMigrated(),
      error: () => { /* the next page load tries again; until then this browser keeps its own choice */ },
    });
  }

  /**
   * Picks up a change made in My-SPA while this tab stayed open: the settings are read again whenever the
   * tab becomes visible, which is what happens when the user comes back from the other app.
   */
  watch(): void {
    if (this.watching) return;
    this.watching = true;
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') void this.refresh();
    });
  }

  private async refresh(): Promise<void> {
    if (!this.synced || !(await firstValueFrom(this.auth.isAuthenticated$.pipe(take(1))))) return;
    this.userApi.getMe().subscribe({
      next: user => {
        this.language.applyServerLanguage(user.language);
        this.theme.applyServerTheme(user.theme);
      },
      error: () => { /* keep what is showing */ },
    });
  }

  /** What this browser holds that the account does not: a differing language and a never-stored appearance. */
  private localChoices(user: PortalUser): Partial<PortalUser> {
    const choices: Partial<PortalUser> = {};
    const language = this.language.savedLanguage;
    if (language && language !== user.language) choices.language = language;
    const theme = this.theme.storedPreference;
    if (theme && !user.theme) choices.theme = theme;
    return choices;
  }

  private migrated(): boolean {
    try { return localStorage.getItem(MIGRATED_KEY) === '1'; } catch { return true; }
  }

  private markMigrated(): void {
    try { localStorage.setItem(MIGRATED_KEY, '1'); } catch { /* storage unavailable */ }
  }
}
